import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Building } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import { EarningsAccountsApi, type EarningsAccount } from '@/services/earnings/earningsAccountsApi'

export interface EarningsAccountSelectProps {
  /** Earnings account id, or '' for no selection. */
  value: string
  onChange: (earningsAccountId: string) => void
  /** Label for the empty choice, offered first to clear the selection. */
  anyLabel?: string
  /** Narrows the publishers offered, e.g. to the ones a targeting reaches. */
  include?: (account: EarningsAccount) => boolean
  disabled?: boolean
  className?: string
}

/**
 * Searchable publisher (earnings account) picker. With `anyLabel` it serves
 * as a list filter, where '' means "not filtered".
 */
export function EarningsAccountSelect({ anyLabel, include, ...props }: EarningsAccountSelectProps) {
  const { t } = useTranslation()

  // Same key as the site editor's publisher field, so both read one cached list.
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['earningsAccounts'],
    queryFn: () => EarningsAccountsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })
  const options = useMemo(
    () =>
      accounts
        .filter((a) => !include || include(a))
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts, include]
  )

  return (
    <SearchSelect
      {...props}
      options={options}
      placeholder={anyLabel ?? t('sites.selectPublisher')}
      clearable={!!anyLabel}
      icon={Building}
      loading={isLoading}
      searchPlaceholder={t('publishers.searchPublishers')}
      emptyText={t('publishers.noPublishersFound')}
    />
  )
}
